import React, { useState, useEffect, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { supabase } from '@/integrations/supabase/client';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { generateProfileName } from '@/utils/profileNameGenerator';
import DesktopBehavioralChart from '@/components/charts/DesktopBehavioralChart';
import { TRAIT_COLORS } from '@/lib/trait-colors';
import CategorizedWordsDisplay from './CategorizedWordsDisplay';

import { User, Briefcase, Sparkles, Download, Loader2, ChevronLeft, ChevronRight, RefreshCw, Share2, Check, Brain, Clock } from 'lucide-react';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { exportModalToPDF } from '@/utils/modalPdfExport';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { copyToClipboard, BASE_URL } from '@/utils/registrationLinks';
import { sigmaToPercentile } from '@/utils/personalityCalculations';
import { getProfileType } from '@/utils/profileTypeMapping';
import { BehavioralFactor, BehavioralScores } from '@/types/resultsTypes';
import AIInsightsLoadingState from '@/components/results/states/AIInsightsLoadingState';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { calculateM21Scores } from '@/utils/m21Scoring';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  assessment_type?: string;
  assessment_variant?: string;
  assessment_metadata?: {
    language?: string;
    duration_seconds?: number;
    [key: string]: any;
  };
  cached_insights?: Record<string, AIInsightsResponse & { generated_at: string }>;
}

interface CognitiveData {
  id: string;
  full_name: string;
  email: string;
  total_questions: number;
  correct_answers: number;
  time_remaining: number;
  verbal_score: number;
  numerical_score: number;
  abstract_score: number;
  overall_percentile: number;
  completed_at: string;
}

interface UnifiedProfileModalProps {
  submission: SurveySubmission | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmissionUpdate?: (updatedSubmission: SurveySubmission) => void;
}

interface TraitInsight {
  strength?: string;
  challenge?: string;
  modification?: string; // For work persona behavioral modifications
}

interface AIInsightsResponse {
  // New format
  trait_insights?: TraitInsight[];
  selfInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[]; // Legacy fallback
  };
  workInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[]; // Legacy fallback
  };
  // Legacy format
  summary_points?: string[];
}

const UnifiedProfileModal: React.FC<UnifiedProfileModalProps> = ({ 
  submission, 
  isOpen, 
  onClose,
  onSubmissionUpdate
}) => {
  const [cognitiveData, setCognitiveData] = useState<CognitiveData | null>(null);
  const [loadingCognitive, setLoadingCognitive] = useState(false);
  const [aiInsights, setAiInsights] = useState<AIInsightsResponse | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [workBehaviorIndex, setWorkBehaviorIndex] = useState(0); // 0 = current, 1+ = history
  const [insightsWorkBehaviorIndex, setInsightsWorkBehaviorIndex] = useState(0); // Track which work behavior index insights were generated for
  const [scoringModel, setScoringModel] = useState<'m2' | 'm21'>('m2'); // M2 vs M2.1 scoring toggle
  const [linkCopied, setLinkCopied] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  
  // Track which insights have been generated this session to prevent duplicate API calls
  const generatedInsightsRef = useRef<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const isMobile = useIsMobile();
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const { data: isSuperAdmin } = useSuperAdminCheck();
  
  // Check if this is an M2 assessment (supports M2.1 toggle)
  const isM2Assessment = submission?.assessment_variant === 'model2';

  const handleShareProfile = async () => {
    if (!submission) return;
    // Create URL-friendly slug from candidate name
    const nameSlug = submission.full_name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
      .substring(0, 50) || 'profile';
    const shareUrl = `${BASE_URL}/profile/${nameSlug}/${submission.id}`;
    const success = await copyToClipboard(shareUrl);
    
    if (success) {
      setLinkCopied(true);
      toast({
        title: "Link copied!",
        description: `Shareable profile link copied to clipboard.`
      });
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard.",
        variant: "destructive"
      });
    }
  };
  
  // Helper to calculate M2.1 chart data from selected word IDs
  const calculateM21ChartData = (selectedWordIds: number[]) => {
    const m21Scores = calculateM21Scores(new Set(selectedWordIds));
    return [
      { name: 'D', fullName: 'Drive', value: m21Scores.dominance, color: TRAIT_COLORS.dominance },
      { name: 'C', fullName: 'Connection', value: m21Scores.extraversion, color: TRAIT_COLORS.extraversion },
      { name: 'T', fullName: 'Task', value: m21Scores.patience, color: TRAIT_COLORS.patience },
      { name: 'O', fullName: 'Order', value: m21Scores.formality, color: TRAIT_COLORS.formality },
      { name: 'S', fullName: 'Style', value: m21Scores.objectivity, color: TRAIT_COLORS.objectivity }
    ];
  };
  const { swipeStyles, isDragging } = useSwipeToClose({ 
    onClose, 
    enabled: isMobile && isOpen,
    threshold: 120 
  });

  const handleExportClick = () => {
    if (!submission) return;
    setShowContactInfo(false); // Reset to default (unchecked)
    setShowExportModal(true);
  };

  const handleConfirmExport = async () => {
    if (!submission) return;
    
    setShowExportModal(false);
    setIsExporting(true);
    try {
      await exportModalToPDF(submission, aiInsights, cognitiveData, showContactInfo);
      toast({
        title: "PDF exported",
        description: `Profile report for ${submission.full_name} has been downloaded.`
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export failed",
        description: "Could not generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Helper function to create behavioral factors from scores
  // Helper function to calculate personality profile without using hooks
  const calculateProfileType = (scores: BehavioralScores): { type: string; description: string } => {
    const percentileThreshold = 50;
    const dPercentile = sigmaToPercentile(scores.dominance);
    const iPercentile = sigmaToPercentile(scores.extraversion);
    const sPercentile = sigmaToPercentile(scores.patience);
    const cPercentile = sigmaToPercentile(scores.formality);
    
    const d = dPercentile >= percentileThreshold ? 'H' : 'L';
    const i = iPercentile >= percentileThreshold ? 'H' : 'L';
    const s = sPercentile >= percentileThreshold ? 'H' : 'L';
    const c = cPercentile >= percentileThreshold ? 'H' : 'L';
    
    let profileKey = `${d}${i}${s}${c}`;
    
    // Handle HHLL sub-classification
    if (profileKey === 'HHLL') {
      const highestIsDominance = scores.dominance >= scores.extraversion;
      const structureOverPatience = scores.formality > scores.patience;
      if (highestIsDominance) {
        profileKey = structureOverPatience ? 'HHLL-AHDH' : 'HHLL-AHDL';
      } else {
        profileKey = structureOverPatience ? 'HHLL-BHDH' : 'HHLL-BHDL';
      }
    }
    
    return getProfileType(profileKey);
  };

  const createBehavioralFactors = (scores: any): BehavioralFactor[] => {
    const calculated = calculateBehavioralScores(scores);
    return [
      {
        name: 'Drive',
        key: 'dominance',
        score: calculated.aScore,
        percentile: calculated.aPercentile,
        description: '',
        color: TRAIT_COLORS.dominance,
        insights: ''
      },
      {
        name: 'Connection',
        key: 'extraversion',
        score: calculated.bScore,
        percentile: calculated.bPercentile,
        description: '',
        color: TRAIT_COLORS.extraversion,
        insights: ''
      },
      {
        name: 'Task',
        key: 'patience',
        score: calculated.cScore,
        percentile: calculated.cPercentile,
        description: '',
        color: TRAIT_COLORS.patience,
        insights: ''
      },
      {
        name: 'Order',
        key: 'formality',
        score: calculated.dScore,
        percentile: calculated.dPercentile,
        description: '',
        color: TRAIT_COLORS.formality,
        insights: ''
      },
      {
        name: 'Style',
        key: 'objectivity',
        score: calculated.eScore,
        percentile: calculated.ePercentile,
        description: '',
        color: TRAIT_COLORS.objectivity,
        insights: ''
      }
    ];
  };

  useEffect(() => {
    const fetchCognitiveData = async () => {
      if (!submission?.email) return;
      
      setLoadingCognitive(true);
      try {
        const { data, error } = await supabase
          .from('cognitive_submissions')
          .select('*')
          .eq('email', submission.email)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          setCognitiveData(data);
        }
      } catch (error) {
        console.error('Error fetching cognitive data:', error);
      } finally {
        setLoadingCognitive(false);
      }
    };

    if (isOpen && submission) {
      fetchCognitiveData();
    }
  }, [isOpen, submission]);

  useEffect(() => {
    const generateAIInsights = async () => {
      if (!submission || !isOpen) return;

      const cacheKey = `${submission.id}-${language}`;

      // Check for cached insights first
      const cachedForLanguage = submission.cached_insights?.[language];
      if (cachedForLanguage) {
        console.log('Using cached AI insights for language:', language);
        setAiInsights(cachedForLanguage);
        setInsightsWorkBehaviorIndex(0); // Cached insights are always for current (index 0)
        setLoadingInsights(false);
        return;
      }

      // Check if we already generated insights this session (prevents duplicate API calls)
      if (generatedInsightsRef.current.has(cacheKey)) {
        console.log('Insights already generated this session for:', cacheKey);
        return;
      }

      setLoadingInsights(true);
      try {
      // Detect assessment structure - robust null checking
      const hasSelfBehavior = submission.scores?.selfBehavior != null && 
        typeof submission.scores.selfBehavior === 'object';
      const hasWorkBehavior = submission.scores?.workBehavior != null && 
        typeof submission.scores.workBehavior === 'object';
      const isDualAssessment = hasSelfBehavior && hasWorkBehavior;

      let body;

        if (isDualAssessment) {
          // Dual assessment mode
          const selfFactors = createBehavioralFactors(submission.scores.selfBehavior);
          const workFactors = createBehavioralFactors(submission.scores.workBehavior);
          
          const selfScores = calculateBehavioralScores(submission.scores.selfBehavior);
          const workScores = calculateBehavioralScores(submission.scores.workBehavior);

          const selfProfile = calculateProfileType({
            dominance: selfScores.aScore,
            extraversion: selfScores.bScore,
            patience: selfScores.cScore,
            formality: selfScores.dScore
          });

          const workProfile = calculateProfileType({
            dominance: workScores.aScore,
            extraversion: workScores.bScore,
            patience: workScores.cScore,
            formality: workScores.dScore
          });

          body = {
            isDualAssessment: true,
            selfData: {
              personalityProfile: selfProfile,
              behavioralFactors: selfFactors,
              selectedWords: submission.scores.selfBehavior.selectedWordsText || []
            },
            workData: {
              personalityProfile: workProfile,
              behavioralFactors: workFactors,
              selectedWords: submission.scores.workBehavior.selectedWordsText || []
            },
            language
          };
        } else {
          // Single assessment mode
          const factors = createBehavioralFactors(submission.scores);
          const scores = calculateBehavioralScores(submission.scores);
          
          const profile = calculateProfileType({
            dominance: scores.aScore,
            extraversion: scores.bScore,
            patience: scores.cScore,
            formality: scores.dScore
          });

          body = {
            isDualAssessment: false,
            personalityProfile: profile,
            scores: {
              dominance: scores.aScore,
              extraversion: scores.bScore,
              patience: scores.cScore,
              formality: scores.dScore,
              objectivity: scores.eScore
            },
            selectedWords: submission.selected_words_text || [],
            behavioralFactors: factors,
            language
          };
        }

        const { data, error } = await supabase.functions.invoke('generate-personality-insights', {
          body
        });

        if (!error && data) {
          setAiInsights(data);
          setInsightsWorkBehaviorIndex(0); // Initial generation is always for current (index 0)
          
          // Mark as generated this session
          generatedInsightsRef.current.add(cacheKey);
          
          // Cache insights to database
          const updatedCache = {
            ...(submission.cached_insights || {}),
            [language]: {
              ...data,
              generated_at: new Date().toISOString()
            }
          };
          
          // Save to database and update parent state
          supabase
            .from('survey_submissions')
            .update({ cached_insights: updatedCache })
            .eq('id', submission.id)
            .then(({ error: updateError }) => {
              if (updateError) {
                console.error('Error caching AI insights:', updateError);
              } else {
                console.log('AI insights cached for language:', language);
                // Notify parent to update its state with cached insights
                if (onSubmissionUpdate) {
                  onSubmissionUpdate({
                    ...submission,
                    cached_insights: updatedCache
                  });
                }
              }
            });
        }
      } catch (error) {
        console.error('Error generating AI insights:', error);
      } finally {
        setLoadingInsights(false);
      }
    };

    generateAIInsights();
  }, [isOpen, submission?.id, language]);

  // Handler to regenerate insights for the currently selected work behavior version
  const handleRegenerateInsightsForSelectedWorkBehavior = async () => {
    if (!submission) return;

    // Get the work behavior data for the selected index
    const getWorkBehaviorForIndex = () => {
      if (workBehaviorIndex === 0) {
        return {
          scores: submission.scores.workBehavior?.scores || submission.scores.workBehavior,
          selectedWordsText: submission.selected_words_text?.work || submission.scores.workBehavior?.selectedWordsText || []
        };
      } else {
        const workBehaviorHistory = submission.scores?.workBehaviorHistory || [];
        const historyIdx = workBehaviorHistory.length - workBehaviorIndex;
        if (historyIdx >= 0 && historyIdx < workBehaviorHistory.length) {
          const historyEntry = workBehaviorHistory[historyIdx];
          return {
            scores: historyEntry.scores,
            selectedWordsText: historyEntry.selectedWordsText || []
          };
        }
        return null;
      }
    };

    const workBehaviorData = getWorkBehaviorForIndex();
    if (!workBehaviorData) return;

    setLoadingInsights(true);
    try {
      const selfFactors = createBehavioralFactors(submission.scores.selfBehavior);
      const workFactors = createBehavioralFactors(workBehaviorData.scores);
      
      const selfScores = calculateBehavioralScores(submission.scores.selfBehavior);
      const workScores = calculateBehavioralScores(workBehaviorData.scores);

      const selfProfile = calculateProfileType({
        dominance: selfScores.aScore,
        extraversion: selfScores.bScore,
        patience: selfScores.cScore,
        formality: selfScores.dScore
      });

      const workProfile = calculateProfileType({
        dominance: workScores.aScore,
        extraversion: workScores.bScore,
        patience: workScores.cScore,
        formality: workScores.dScore
      });

      const body = {
        isDualAssessment: true,
        selfData: {
          personalityProfile: selfProfile,
          behavioralFactors: selfFactors,
          selectedWords: submission.scores.selfBehavior.selectedWordsText || []
        },
        workData: {
          personalityProfile: workProfile,
          behavioralFactors: workFactors,
          selectedWords: workBehaviorData.selectedWordsText
        },
        language
      };

      const { data, error } = await supabase.functions.invoke('generate-personality-insights', {
        body
      });

      if (!error && data) {
        setAiInsights(data);
        setInsightsWorkBehaviorIndex(workBehaviorIndex);
        // Note: DON'T cache historical insights - they're temporary views
        console.log('Regenerated insights for work behavior index:', workBehaviorIndex);
      }
    } catch (error) {
      console.error('Error regenerating AI insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  if (!submission) return null;

  // Detect assessment structure - robust null checking
  const hasSelfBehavior = submission.scores?.selfBehavior != null && 
    typeof submission.scores.selfBehavior === 'object';
  const hasWorkBehavior = submission.scores?.workBehavior != null && 
    typeof submission.scores.workBehavior === 'object';
  const isDualAssessment = hasSelfBehavior && hasWorkBehavior;
  const isWorkBehaviorOnly = !hasSelfBehavior && hasWorkBehavior;
  
  // Get work behavior history
  const workBehaviorHistory: any[] = submission.scores?.workBehaviorHistory || [];
  const hasWorkHistory = workBehaviorHistory.length > 0;
  const totalWorkVersions = hasWorkHistory ? workBehaviorHistory.length + 1 : 1; // +1 for current

  // Get the work behavior data based on selected index
  // Index 0 = current (most recent), 1+ = historical (older)
  const getWorkBehaviorData = () => {
    // Support both dual assessment and work-behavior-only scenarios
    if (!isDualAssessment && !isWorkBehaviorOnly) return null;
    
    if (workBehaviorIndex === 0) {
      // Current (most recent) work behavior
      return {
        scores: submission.scores.workBehavior?.scores || submission.scores.workBehavior,
        selectedWords: submission.selected_words?.work || submission.scores.workBehavior?.selectedWords || [],
        selectedWordsText: submission.selected_words_text?.work || submission.scores.workBehavior?.selectedWordsText || [],
        completedAt: submission.completed_at
      };
    } else {
      // Historical work behavior (index 1 = most recent in history, etc.)
      const historyIdx = workBehaviorHistory.length - workBehaviorIndex;
      if (historyIdx >= 0 && historyIdx < workBehaviorHistory.length) {
        const historyEntry = workBehaviorHistory[historyIdx];
        return {
          scores: historyEntry.scores,
          selectedWords: historyEntry.selectedWords || [],
          selectedWordsText: historyEntry.selectedWordsText || [],
          completedAt: historyEntry.completedAt
        };
      }
      return null;
    }
  };

  const selectedWorkBehavior = getWorkBehaviorData();

  let behavioralData;
  let selfBehavioralData = null;
  let workBehavioralData = null;
  let selfChartData = null;
  let workChartData = null;
  let selfWordsCount = 0;
  let workWordsCount = 0;
  let selfWordsText: string[] = [];
  let workWordsText: string[] = [];
  let hasSelfData = false;
  let hasWorkData = false;

  // Detect M3 assessment - M3 stores percentiles directly
  const isM3Assessment = submission?.assessment_variant === 'model3';

  if (isDualAssessment || isWorkBehaviorOnly) {
    // Process self behavior data only if it exists
    if (hasSelfBehavior) {
      const selfScoresData = submission.scores.selfBehavior?.scores || submission.scores.selfBehavior;
      
      // For M3, use stored percentiles directly; for others, calculate from z-scores
      let selfScores;
      if (isM3Assessment && selfScoresData?.dominancePercentile !== undefined) {
        // M3 stores percentiles directly - use them
        selfScores = {
          aPercentile: selfScoresData.dominancePercentile,
          bPercentile: selfScoresData.extraversionPercentile,
          cPercentile: selfScoresData.patiencePercentile,
          dPercentile: selfScoresData.formalityPercentile,
          ePercentile: selfScoresData.objectivityPercentile,
          aScore: selfScoresData.dominance ?? 0,
          bScore: selfScoresData.extraversion ?? 0,
          cScore: selfScoresData.patience ?? 0,
          dScore: selfScoresData.formality ?? 0,
          eScore: selfScoresData.objectivity ?? 0
        };
      } else {
        selfScores = calculateBehavioralScores(submission.scores.selfBehavior);
      }
      
      // Check submission.selected_words.self first (M2 format), fallback to scores.selfBehavior.selectedWords (legacy)
      const selfWordIds = submission.selected_words?.self || submission.scores.selfBehavior?.selectedWords || [];
      selfWordsCount = selfWordIds.length;
      // Get selected words text - M2 format or legacy
      selfWordsText = submission.selected_words_text?.self || submission.scores.selfBehavior?.selectedWordsText || [];
      hasSelfData = true;
      
      // Use M2.1 scoring if toggled on for M2 assessments
      if (scoringModel === 'm21' && isM2Assessment) {
        selfChartData = calculateM21ChartData(selfWordIds);
      } else {
        selfChartData = [
          { name: 'D', fullName: 'Drive', value: selfScores.aPercentile, color: TRAIT_COLORS.dominance },
          { name: 'C', fullName: 'Connection', value: selfScores.bPercentile, color: TRAIT_COLORS.extraversion },
          { name: 'T', fullName: 'Task', value: selfScores.cPercentile, color: TRAIT_COLORS.patience },
          { name: 'O', fullName: 'Order', value: selfScores.dPercentile, color: TRAIT_COLORS.formality },
          { name: 'S', fullName: 'Style', value: selfScores.ePercentile, color: TRAIT_COLORS.objectivity }
        ];
      }

      selfBehavioralData = {
        dominance: { score: selfScores.aScore, percentile: selfScores.aPercentile },
        extraversion: { score: selfScores.bScore, percentile: selfScores.bPercentile },
        patience: { score: selfScores.cScore, percentile: selfScores.cPercentile },
        formality: { score: selfScores.dScore, percentile: selfScores.dPercentile },
        objectivity: { score: selfScores.eScore, percentile: selfScores.ePercentile }
      };
    }

    // Process work behavior data - use selected work behavior (supports history navigation)
    if (selectedWorkBehavior) {
      const workScoresData = selectedWorkBehavior.scores;
      
      // For M3, use stored percentiles directly; for others, calculate from z-scores
      let workScores;
      if (isM3Assessment && workScoresData?.dominancePercentile !== undefined) {
        // M3 stores percentiles directly - use them
        workScores = {
          aPercentile: workScoresData.dominancePercentile,
          bPercentile: workScoresData.extraversionPercentile,
          cPercentile: workScoresData.patiencePercentile,
          dPercentile: workScoresData.formalityPercentile,
          ePercentile: workScoresData.objectivityPercentile,
          aScore: workScoresData.dominance ?? 0,
          bScore: workScoresData.extraversion ?? 0,
          cScore: workScoresData.patience ?? 0,
          dScore: workScoresData.formality ?? 0,
          eScore: workScoresData.objectivity ?? 0
        };
      } else {
        workScores = calculateBehavioralScores(selectedWorkBehavior.scores);
      }
      
      const workWordIds = selectedWorkBehavior.selectedWords || [];
      workWordsCount = workWordIds.length;
      workWordsText = selectedWorkBehavior.selectedWordsText || [];
      hasWorkData = true;
      
      // Use M2.1 scoring if toggled on for M2 assessments
      if (scoringModel === 'm21' && isM2Assessment) {
        workChartData = calculateM21ChartData(workWordIds);
      } else {
        workChartData = [
          { name: 'D', fullName: 'Drive', value: workScores.aPercentile, color: TRAIT_COLORS.dominance },
          { name: 'C', fullName: 'Connection', value: workScores.bPercentile, color: TRAIT_COLORS.extraversion },
          { name: 'T', fullName: 'Task', value: workScores.cPercentile, color: TRAIT_COLORS.patience },
          { name: 'O', fullName: 'Order', value: workScores.dPercentile, color: TRAIT_COLORS.formality },
          { name: 'S', fullName: 'Style', value: workScores.ePercentile, color: TRAIT_COLORS.objectivity }
        ];
      }

      // Use work behavior for insights
      behavioralData = {
        dominance: { score: workScores.aScore, percentile: workScores.aPercentile },
        extraversion: { score: workScores.bScore, percentile: workScores.bPercentile },
        patience: { score: workScores.cScore, percentile: workScores.cPercentile },
        formality: { score: workScores.dScore, percentile: workScores.dPercentile },
        objectivity: { score: workScores.eScore, percentile: workScores.ePercentile }
      };
    }
  } else {
    // Single assessment - check if it's work behavior based on assessment_type or treat as self
    const scoresData = submission.scores;
    
    // For M3, use stored percentiles directly; for others, calculate from z-scores
    let scores;
    if (isM3Assessment && scoresData?.dominancePercentile !== undefined) {
      scores = {
        aPercentile: scoresData.dominancePercentile,
        bPercentile: scoresData.extraversionPercentile,
        cPercentile: scoresData.patiencePercentile,
        dPercentile: scoresData.formalityPercentile,
        ePercentile: scoresData.objectivityPercentile,
        aScore: scoresData.dominance ?? 0,
        bScore: scoresData.extraversion ?? 0,
        cScore: scoresData.patience ?? 0,
        dScore: scoresData.formality ?? 0,
        eScore: scoresData.objectivity ?? 0
      };
    } else {
      scores = calculateBehavioralScores(submission.scores);
    }
    
    const wordsCount = submission.selected_words?.length || 0;
    const wordsText = submission.selected_words_text || [];
    
    const chartData = [
      { name: 'D', fullName: 'Drive', value: scores.aPercentile, color: TRAIT_COLORS.dominance },
      { name: 'C', fullName: 'Connection', value: scores.bPercentile, color: TRAIT_COLORS.extraversion },
      { name: 'T', fullName: 'Task', value: scores.cPercentile, color: TRAIT_COLORS.patience },
      { name: 'O', fullName: 'Order', value: scores.dPercentile, color: TRAIT_COLORS.formality },
      { name: 'S', fullName: 'Style', value: scores.ePercentile, color: TRAIT_COLORS.objectivity }
    ];

    // Check assessment_type to determine which chart to populate
    if (submission.assessment_type === 'work_behavior') {
      workChartData = chartData;
      workWordsCount = wordsCount;
      workWordsText = Array.isArray(wordsText) ? wordsText : [];
      hasWorkData = true;
    } else {
      // Default to self assessment for older behavioral assessments
      selfChartData = chartData;
      selfWordsCount = wordsCount;
      selfWordsText = Array.isArray(wordsText) ? wordsText : [];
      hasSelfData = true;
    }

    behavioralData = {
      dominance: { score: scores.aScore, percentile: scores.aPercentile },
      extraversion: { score: scores.bScore, percentile: scores.bPercentile },
      patience: { score: scores.cScore, percentile: scores.cPercentile },
      formality: { score: scores.dScore, percentile: scores.dPercentile },
      objectivity: { score: scores.eScore, percentile: scores.ePercentile }
    };
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="!grid-none flex flex-col max-w-4xl p-0 bg-background border-0 overflow-hidden md:max-h-[90vh] md:rounded-lg h-full md:h-auto inset-0 md:inset-auto translate-x-0 translate-y-0 md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%] rounded-none md:rounded-lg"
        style={isMobile ? swipeStyles : undefined}
      >
        {/* Swipe indicator for mobile */}
        {isMobile && (
          <div className="flex-shrink-0 pt-2 pb-1 flex justify-center bg-background">
            <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full" />
          </div>
        )}
        
        {/* Fixed Header with candidate info */}
        <DialogHeader className="flex-shrink-0 px-4 md:px-6 pt-4 md:pt-6 pb-4 bg-background border-b border-border">
          <div className="flex items-center justify-between pr-10">
            <div className="flex items-center gap-3 min-w-0">
              <DialogTitle className="text-xl md:text-2xl font-semibold text-foreground flex-shrink-0">
                Profile Analysis
              </DialogTitle>
              {submission && (
                <>
                  <span className="text-muted-foreground/40 text-xl font-light flex-shrink-0">/</span>
                  <span className="text-lg md:text-xl font-medium text-foreground truncate">
                    {submission.full_name}
                  </span>
                </>
              )}
              {scoringModel === 'm21' && (
                <Badge className="bg-amber-100 text-amber-700 text-[10px] border-amber-200 flex-shrink-0">
                  M2.1 Scoring
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* M2/M2.1 Toggle - Super Admin only, M2 assessments only */}
              {isSuperAdmin && isM2Assessment && (
                <div className="flex items-center gap-1.5 mr-2">
                  <span className={`text-xs ${scoringModel === 'm2' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    M2
                  </span>
                  <Switch
                    checked={scoringModel === 'm21'}
                    onCheckedChange={(checked) => setScoringModel(checked ? 'm21' : 'm2')}
                    className="data-[state=checked]:bg-amber-600 h-5 w-9"
                  />
                  <span className={`text-xs ${scoringModel === 'm21' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    M2.1
                  </span>
                </div>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShareProfile}
                className="h-9 w-9"
                title="Share profile link"
              >
                {linkCopied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Share2 className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleExportClick}
                disabled={isExporting || loadingInsights}
                className="h-9 w-9"
                title="Download PDF"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* Candidate meta info */}
          {submission && (() => {
            const behavioralSeconds = submission.assessment_metadata?.duration_seconds ?? null;
            const cognitiveSeconds = cognitiveData 
              ? Math.max(0, 12 * 60 - (cognitiveData.time_remaining ?? 0))
              : null;
            const totalSeconds = (behavioralSeconds ?? 0) + (cognitiveSeconds ?? 0);
            const hasDuration = behavioralSeconds != null || cognitiveSeconds != null;
            const fmtDur = (s: number) => `${Math.floor(s / 60)}m ${s % 60}s`;

            return (
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
                <span className="italic">{generateProfileName(submission)}</span>
                <span>·</span>
                <span className="truncate">{submission.email}</span>
                <span>·</span>
                <span>{new Date(submission.completed_at).toLocaleDateString()} {new Date(submission.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                <span>·</span>
                <span>{submission.assessment_metadata?.language === 'pt' ? 'Português' : submission.assessment_metadata?.language === 'es' ? 'Español' : 'English'}</span>
                {hasDuration && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {behavioralSeconds != null && (
                        <span title="Behavioral duration">B: {fmtDur(behavioralSeconds)}</span>
                      )}
                      {behavioralSeconds != null && cognitiveSeconds != null && <span>|</span>}
                      {cognitiveSeconds != null && (
                        <span title="Cognitive duration">C: {fmtDur(cognitiveSeconds)}</span>
                      )}
                      {behavioralSeconds != null && cognitiveSeconds != null && (
                        <>
                          <span>|</span>
                          <span className="font-medium" title="Total duration">Σ {fmtDur(totalSeconds)}</span>
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>
            );
          })()}
          <DialogDescription className="sr-only">Detailed profile analysis</DialogDescription>
        </DialogHeader>

        {/* Scrollable Content with overscroll containment */}
        <div 
          className="flex-1 overflow-y-auto min-h-0 overscroll-contain pb-safe"
          data-swipe-container
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="px-4 md:px-6 py-4 md:py-6 pb-8 md:pb-6 space-y-4 md:space-y-6">

            {/* Cognitive Results Section */}
            {cognitiveData && (
              <div className="border-b border-border pb-4 md:pb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                  <h3 className="text-base md:text-lg font-semibold text-foreground">Cognitive Assessment</h3>
                  <span className="text-xs text-muted-foreground ml-auto">
                    {format(new Date(cognitiveData.completed_at), 'MMM d, yyyy')}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className="col-span-2 md:col-span-1 bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Overall Percentile</p>
                    <p className="text-2xl font-bold text-foreground">{cognitiveData.overall_percentile}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {cognitiveData.correct_answers}/{cognitiveData.total_questions} correct
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Verbal</p>
                    <p className="text-xl font-semibold text-foreground">{cognitiveData.verbal_score}%</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Numerical</p>
                    <p className="text-xl font-semibold text-foreground">{cognitiveData.numerical_score}%</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Abstract</p>
                    <p className="text-xl font-semibold text-foreground">{cognitiveData.abstract_score}%</p>
                  </div>
                </div>
              </div>
            )}
            {loadingCognitive && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading cognitive results...</span>
              </div>
            )}

            {/* Charts - Stack on mobile, grid on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* Self Assessment Chart */}
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                  <div>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">Self Assessment</h3>
                    {hasSelfData ? (
                      isSuperAdmin ? (
                        <HoverCard openDelay={200}>
                          <HoverCardTrigger asChild>
                            <p className="text-xs md:text-sm text-muted-foreground cursor-help hover:text-foreground transition-colors">
                              Based on <span className="underline decoration-dotted">{selfWordsCount} selected words</span>
                              {submission?.completed_at && <span> • {format(new Date(submission.completed_at), 'MMM d, yyyy')}</span>}
                            </p>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-96 max-h-80 overflow-y-auto" side="bottom" align="start">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium">Selected Words ({selfWordsCount})</h4>
                              {selfWordsText.length > 0 ? (
                                <CategorizedWordsDisplay words={selfWordsText} t={t} />
                              ) : (
                                <p className="text-xs text-muted-foreground">Word details not available</p>
                              )}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      ) : (
                        <p className="text-xs md:text-sm text-muted-foreground">
                          Based on {selfWordsCount} selected words
                          {submission?.completed_at && <span> • {format(new Date(submission.completed_at), 'MMM d, yyyy')}</span>}
                        </p>
                      )
                    ) : (
                      <p className="text-xs md:text-sm text-muted-foreground">No data available</p>
                    )}
                  </div>
                </div>
                {hasSelfData ? (
                  <DesktopBehavioralChart data={selfChartData} height={isMobile ? 180 : 200} />
                ) : (
                  <div className="h-[180px] md:h-[200px] border border-border/30 rounded-lg flex items-center justify-center bg-muted/20">
                    <p className="text-xs md:text-sm text-muted-foreground">Self assessment data not available</p>
                  </div>
                )}
              </div>

              {/* Work Behavior Chart */}
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base md:text-lg font-semibold text-foreground">Work Behavior</h3>
                      {/* History Navigation */}
                      {hasWorkHistory && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={workBehaviorIndex >= totalWorkVersions - 1}
                            onClick={() => setWorkBehaviorIndex(i => i + 1)}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-xs text-muted-foreground min-w-[60px] text-center">
                            {workBehaviorIndex === 0 ? 'Current' : `${workBehaviorIndex} of ${totalWorkVersions - 1}`}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={workBehaviorIndex === 0}
                            onClick={() => setWorkBehaviorIndex(i => i - 1)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {hasWorkData ? (
                      isSuperAdmin ? (
                        <HoverCard openDelay={200}>
                          <HoverCardTrigger asChild>
                            <p className="text-xs md:text-sm text-muted-foreground cursor-help hover:text-foreground transition-colors">
                              Based on <span className="underline decoration-dotted">{workWordsCount} selected words</span>
                              {selectedWorkBehavior?.completedAt && <span> • {format(new Date(selectedWorkBehavior.completedAt), 'MMM d, yyyy')}</span>}
                            </p>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-96 max-h-80 overflow-y-auto" side="bottom" align="start">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium">Selected Words ({workWordsCount})</h4>
                              {workWordsText.length > 0 ? (
                                <CategorizedWordsDisplay words={workWordsText} t={t} />
                              ) : (
                                <p className="text-xs text-muted-foreground">Word details not available</p>
                              )}
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      ) : (
                        <p className="text-xs md:text-sm text-muted-foreground">
                          Based on {workWordsCount} selected words
                          {selectedWorkBehavior?.completedAt && <span> • {format(new Date(selectedWorkBehavior.completedAt), 'MMM d, yyyy')}</span>}
                        </p>
                      )
                    ) : (
                      <p className="text-xs md:text-sm text-muted-foreground">No data available</p>
                    )}
                  </div>
                </div>
                {hasWorkData ? (
                  <DesktopBehavioralChart data={workChartData} height={isMobile ? 180 : 200} />
                ) : (
                  <div className="h-[180px] md:h-[200px] border border-border/30 rounded-lg flex items-center justify-center bg-muted/20">
                    <p className="text-xs md:text-sm text-muted-foreground">Work behavior data not available</p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 md:mt-8 border-t border-border pt-6 md:pt-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg md:text-xl font-semibold text-foreground">AI Personality Insights</h3>
                
                {/* Show refresh button ONLY when viewing different work behavior than insights were generated for */}
                {isDualAssessment && workBehaviorIndex !== insightsWorkBehaviorIndex && !loadingInsights && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateInsightsForSelectedWorkBehavior}
                    className="ml-auto flex items-center gap-1.5 text-xs"
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span>Refresh for this version</span>
                  </Button>
                )}
              </div>
              
              {loadingInsights ? (
                <AIInsightsLoadingState isDualAssessment={isDualAssessment} />
              ) : aiInsights ? (
                <div className="space-y-8 w-full">
                  {isDualAssessment && aiInsights.selfInsights && aiInsights.workInsights ? (
                    <>
                      {/* Self Section */}
                      <div className="space-y-4 w-full">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">🏠</span>
                          <h4 className="text-base md:text-lg font-semibold text-foreground">Your Natural Self</h4>
                        </div>
                        {aiInsights.selfInsights.trait_insights ? (
                          aiInsights.selfInsights.trait_insights.map((insight, index) => (
                            <div 
                              key={index}
                              className="border-l-4 border-foreground pl-3 md:pl-4 py-2"
                            >
                              <p className="text-sm md:text-base text-foreground leading-relaxed">
                                {insight.strength} {insight.challenge}
                              </p>
                            </div>
                          ))
                        ) : aiInsights.selfInsights.summary_points ? (
                          aiInsights.selfInsights.summary_points.map((point, index) => (
                            <div 
                              key={index}
                              className="border-l-4 border-foreground pl-3 md:pl-4 py-2"
                            >
                              <p className="text-sm md:text-base text-foreground leading-relaxed">{point}</p>
                            </div>
                          ))
                        ) : null}
                      </div>

                      {/* Work Section - Behavioral Modifications */}
                      <div className="space-y-4 w-full pt-6 border-t border-border">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">💼</span>
                          <h4 className="text-base md:text-lg font-semibold text-foreground">Your Work Persona</h4>
                        </div>
                        {aiInsights.workInsights.trait_insights ? (
                          aiInsights.workInsights.trait_insights.map((insight, index) => (
                            <div 
                              key={index}
                              className="border-l-4 border-primary pl-3 md:pl-4 py-2"
                            >
                              <p className="text-sm md:text-base text-foreground leading-relaxed">
                                {insight.modification}
                              </p>
                            </div>
                          ))
                        ) : aiInsights.workInsights.summary_points ? (
                          aiInsights.workInsights.summary_points.map((point, index) => (
                            <div 
                              key={index}
                              className="border-l-4 border-primary pl-3 md:pl-4 py-2"
                            >
                              <p className="text-sm md:text-base text-foreground leading-relaxed">{point}</p>
                            </div>
                          ))
                        ) : null}
                      </div>
                    </>
                  ) : aiInsights.trait_insights ? (
                    <div className="space-y-4 w-full">
                      {aiInsights.trait_insights.map((insight, index) => (
                        <div 
                          key={index}
                          className="border-l-4 border-foreground pl-3 md:pl-4 py-2"
                        >
                          <p className="text-sm md:text-base text-foreground leading-relaxed">
                            {insight.strength} {insight.challenge}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : aiInsights.summary_points ? (
                    <div className="space-y-4 w-full">
                      {aiInsights.summary_points.map((point, index) => (
                        <div 
                          key={index}
                          className="border-l-4 border-foreground pl-3 md:pl-4 py-2"
                        >
                          <p className="text-sm md:text-base text-foreground leading-relaxed">{point}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No insights available at this time.
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* PDF Export Confirmation Modal */}
    <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Download PDF Report</DialogTitle>
          <DialogDescription>
            Choose what information to include in the PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-3 py-4">
          <Checkbox 
            id="showContactInfo" 
            checked={showContactInfo}
            onCheckedChange={(checked) => setShowContactInfo(checked === true)}
          />
          <Label 
            htmlFor="showContactInfo" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            Show contact information (full name and email)
          </Label>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmExport}>
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default UnifiedProfileModal;
